import React from 'react';
import s from './Totals.scss';
import get from 'lodash.get';
import { Text } from '@wix/design-system';
import { withTranslation } from '@wix/fe-essentials/i18n';
import { DEFAULT_CURRENCY_CODE, currencyFormat } from '../../../core';
import { DepositType, } from '@wix/ambassador-wix-quotes-web/types';
import { getCurrencyByKey } from '@wix/locale-dataset-javascript';
const AmountToPay = (props) => {
    const { label, value, dataHookPrefix } = props;
    return (React.createElement("div", { className: s.balance },
        React.createElement(Text, { dataHook: `${dataHookPrefix}-label` }, label),
        React.createElement(Text, { className: s.balanceValue, weight: "bold", size: "medium", dataHook: `${dataHookPrefix}-text` }, value)));
};
const Totals = ({ t, invoice, showTaxBreakdown, taxSettingsEnabled, showItemsSubtotalPerTax, taxAdjustmentsEnabled, }) => {
    const { currencyCode: currency } = invoice;
    const { fraction } = getCurrencyByKey(currency ?? DEFAULT_CURRENCY_CODE);
    const subtotal = get(invoice.totals, ['subtotal', 'serializedValue'], 0);
    const discountAmount = get(invoice.totals, ['discountAmount', 'serializedValue'], 0);
    const total = get(invoice.totals, ['total', 'serializedValue'], 0);
    const fees = get(invoice.totals, 'fees') ?? [];
    const discountRate = get(invoice.discount, ['percentage', 'serializedValue']);
    const depositRate = Number(get(invoice.deposit, 'percentage', 0));
    const depositAmount = Number(get(invoice.deposit, 'flatAmount', 0));
    const depositType = get(invoice.deposit, 'type');
    const taxes = get(invoice.taxes, 'taxes') ?? [];
    const taxedTotal = get(invoice.totals, ['taxedAmount', 'serializedValue'], 0);
    const paidAmount = get(invoice.dynamicTotals, ['paidAmount', 'serializedValue'], 0);
    const balance = get(invoice.dynamicTotals, ['balance', 'serializedValue'], 0);
    const format = currencyFormat({
        locale: invoice.locale && invoice.locale.language,
        currency,
    });
    function hasDeposit() {
        return !!depositAmount && depositAmount - paidAmount > 0;
    }
    return (React.createElement("div", { className: s.totals },
        React.createElement("div", { className: s.item, style: { marginBottom: '-5px' } },
            React.createElement(Text, { weight: taxAdjustmentsEnabled ? 'bold' : 'thin', dataHook: "subtotal-label" }, taxSettingsEnabled
                ? t('quote.form.net-total')
                : t('quote.form.sub_total')),
            React.createElement("div", { className: s.totalValue },
                React.createElement(Text, { weight: taxAdjustmentsEnabled ? 'bold' : 'thin', dataHook: "subtotal-text" }, format(subtotal)))),
        fees.length > 0 &&
            fees
                .filter((p) => !!p.price?.serializedValue)
                .map(({ name, price: { serializedValue: price } }) => price !== 0 && (React.createElement("div", { key: `fee-${name}`, className: s.item },
                React.createElement(Text, { weight: "thin", dataHook: `fee-${name}-name` }, name),
                React.createElement("div", { className: s.totalValue },
                    React.createElement(Text, { weight: "thin", dataHook: `fee-${name}-value` }, format(price)))))),
        !!discountRate && (React.createElement("div", { className: s.item },
            React.createElement(Text, { weight: "thin", dataHook: "discount-label" }, `${t('quote.form.discount')} (${discountRate}%)`),
            React.createElement("div", { className: s.totalValue },
                React.createElement(Text, { weight: "thin", dataHook: "discount-text" }, format(discountAmount))))),
        (!showTaxBreakdown || (showTaxBreakdown && taxes.length === 0)) && (React.createElement("div", { className: s.item },
            React.createElement(Text, { weight: "thin", dataHook: "taxes" }, t('price-quote.view.totals.taxes')),
            React.createElement("div", { className: s.totalValue },
                React.createElement(Text, { dataHook: "tax-totals", weight: "thin" }, format(taxes.reduce((sum, tax) => sum + (tax.taxed?.serializedValue ?? 0), 0)))))),
        showTaxBreakdown && taxes.length > 0 && (React.createElement(React.Fragment, null,
            taxAdjustmentsEnabled && (React.createElement(React.Fragment, null,
                React.createElement("hr", { className: s.divider }),
                React.createElement("div", { className: s.item },
                    React.createElement(Text, { weight: "bold", dataHook: "tax-breakdown" }, t('price-quote.view.totals.tax-breakdown'))))),
            taxes
                .filter((tax) => tax.taxed?.serializedValue !== undefined &&
                tax.rate?.serializedValue !== undefined)
                .map(({ taxed: { serializedValue: taxed }, rate: { serializedValue: taxRate }, name, }) => (React.createElement("div", { key: `${name}-${taxRate}`, className: s.item },
                React.createElement(Text, { weight: "thin", dataHook: `tax-${name}-${taxRate}-name` }, t('price-quote.view.totals.tax-rate', {
                    name,
                    rate: taxRate,
                })),
                React.createElement("div", { className: s.totalValue },
                    React.createElement(Text, { dataHook: `tax-${name}-${taxRate}-taxed`, weight: "thin" }, format(taxed)))))),
            taxAdjustmentsEnabled && (React.createElement("div", { className: s.item },
                React.createElement(Text, { weight: "thin", dataHook: "taxes" }, t('price-quote.view.totals.tax-total')),
                React.createElement("div", { className: s.totalValue },
                    React.createElement(Text, { dataHook: "total-text", weight: "thin" }, format(taxedTotal))))))),
        taxAdjustmentsEnabled && showItemsSubtotalPerTax && taxes.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement("hr", { className: s.divider }),
            React.createElement("div", { className: s.item },
                React.createElement(Text, { weight: "bold", dataHook: "taxes" }, t('price-quote.view.totals.items-subtotal-per-tax'))),
            taxes
                .filter((tax) => tax.taxable?.serializedValue !== undefined &&
                tax.rate?.serializedValue !== undefined)
                .map(({ rate: { serializedValue: taxRate }, 
            // eslint-disable-next-line @typescript-eslint/no-shadow
            taxable: { serializedValue: taxedTotal }, name, }) => (React.createElement("div", { key: `${name}-${taxRate}`, className: s.item },
                React.createElement(Text, { weight: "thin", dataHook: `tax-${name}-${taxRate}-name` }, t('price-quote.view.totals.tax-rate', {
                    name,
                    rate: taxRate,
                })),
                React.createElement("div", { className: s.totalValue },
                    React.createElement(Text, { dataHook: `tax-${name}-${taxRate}-taxed`, weight: "thin" }, format(taxedTotal)))))))),
        React.createElement("hr", { className: s.divider }),
        React.createElement("div", { className: s.item },
            React.createElement(Text, { dataHook: "total-label", weight: "thin" }, t('invoice-view:quoteview.uou_total')),
            React.createElement("div", { className: s.totalValue },
                React.createElement(Text, { dataHook: "total-text", weight: "thin" }, format(total)))),
        hasDeposit() && (React.createElement("div", { className: s.item },
            React.createElement(Text, { weight: "thin", dataHook: "deposit-label" }, depositType === DepositType.PERCENTAGE
                ? `${t('quote.form.deposit')} (${Number((depositRate * 100).toFixed(fraction))}%)`
                : `${t('quote.form.deposit')}`),
            React.createElement("div", { className: s.totalValue },
                React.createElement(Text, { weight: "thin", dataHook: "deposit-text" }, format(depositAmount))))),
        React.createElement("div", { className: s.item },
            React.createElement(Text, { dataHook: "amount-paid-label", weight: "thin" }, t('invoice-view:quote.form.amount_paid')),
            React.createElement("div", { className: s.totalValue },
                React.createElement(Text, { dataHook: "amount-paid-total-text", weight: "thin" }, format(paidAmount)))),
        hasDeposit() ? (React.createElement(AmountToPay, { label: t('invoice-view:quote.form.deposit-due'), value: format(depositAmount - paidAmount), dataHookPrefix: "deposit-due" })) : (React.createElement(AmountToPay, { label: t('invoice-view:quote.form.balance'), value: format(balance), dataHookPrefix: "balance" }))));
};
export default withTranslation(['invoice-view', 'locale-data'])(Totals);
//# sourceMappingURL=Totals.js.map